import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500
};

/**
 * Bandholz marquee component.
 *
 * Based on Ritual's native marquee component, with optional hover slowdown
 * controlled by data-pause-on-hover.
 *
 * Reliability adjustments:
 * - Uses direct DOM measurements instead of IntersectionObserver for setup.
 * - Waits for browser layout before the initial measurement.
 * - Uses ResizeObserver when available, with window.resize as a fallback.
 * - Limits hover animation controls to devices that support true hover.
 * - Preserves prefers-reduced-motion handling in the section CSS.
 * - Preserves Ritual's duplication, speed and responsive behavior.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper
 * @property {HTMLElement} content
 * @property {HTMLElement[]} marqueeItems
 *
 * @extends Component<Refs>
 */
class BandholzMarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * Last measured width of the marquee viewport.
   *
   * @type {number | null}
   */
  #marqueeWidth = null;

  /**
   * ResizeObserver used when supported by the browser.
   *
   * @type {ResizeObserver | null}
   */
  #resizeObserver = null;

  /**
   * Whether hover-specific behavior should be enabled on this device.
   *
   * @type {boolean}
   */
  #supportsHover = false;

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;

    if (!marqueeItems.length || !marqueeItems[0]) {
      return;
    }

    /*
     * Give the browser one completed layout frame before measuring.
     * This is particularly useful on mobile Safari where custom elements,
     * fonts and responsive layout may settle after connection.
     */
    await this.#nextFrame();

    const { numberOfCopies } =
      this.#queryNumberOfCopies();

    const speed =
      this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(speed);

    /*
     * Observe the actual component width when ResizeObserver is available.
     * Keep window.resize as a universal fallback and for viewport changes
     * that may not immediately produce a ResizeObserver callback.
     */
    if ('ResizeObserver' in window) {
      this.#resizeObserver =
        new ResizeObserver(this.#handleResize);

      this.#resizeObserver.observe(this);
    }

    window.addEventListener(
      'resize',
      this.#handleResize,
      { passive: true }
    );

    /*
     * Hover slowdown is only useful on devices with a fine pointer and
     * genuine hover support. Touch devices such as iPhones skip this
     * animation-control path entirely.
     */
    this.#supportsHover =
      window.matchMedia?.(
        '(hover: hover) and (pointer: fine)'
      ).matches ?? false;

    if (this.pauseOnHover && this.#supportsHover) {
      this.addEventListener(
        'pointerenter',
        this.#slowDown
      );

      this.addEventListener(
        'pointerleave',
        this.#speedUp
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    window.removeEventListener(
      'resize',
      this.#handleResize
    );

    if (this.#supportsHover) {
      this.removeEventListener(
        'pointerenter',
        this.#slowDown
      );

      this.removeEventListener(
        'pointerleave',
        this.#speedUp
      );
    }

    this.#slowDown.cancel();
    this.#animation?.cancel();
    this.#animation = null;
  }

  /**
   * Whether hover slowdown is enabled for this section.
   *
   * @returns {boolean}
   */
  get pauseOnHover() {
    return (
      this.dataset.pauseOnHover === 'true'
    );
  }

  #slowDown = debounce(() => {
    if (
      !this.pauseOnHover ||
      !this.#supportsHover
    ) {
      return;
    }

    if (this.#animation) {
      return;
    }

    const animation =
      this.refs.wrapper.getAnimations?.()[0];

    if (!animation) {
      return;
    }

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: animation.playbackRate ?? 1,
      to: 0,
      onUpdate: (value) => {
        animation.updatePlaybackRate(value);
      },
      onComplete: () => {
        this.#animation = null;
      }
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp = () => {
    if (
      !this.pauseOnHover ||
      !this.#supportsHover
    ) {
      return;
    }

    this.#slowDown.cancel();

    const animation =
      this.refs.wrapper.getAnimations?.()[0];

    if (
      !animation ||
      animation.playbackRate === 1
    ) {
      return;
    }

    const from =
      this.#animation?.current ??
      animation.playbackRate ??
      0;

    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => {
        animation.updatePlaybackRate(value);
      },
      onComplete: () => {
        this.#animation = null;
      }
    });
  };

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild
      ? lastChild
      : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    const safeValue =
      Number.isFinite(value) && value > 0
        ? value
        : 25;

    this.style.setProperty(
      '--marquee-speed',
      `${safeValue}s`
    );
  }

  /**
   * Measure the component and the first repeated item directly.
   *
   * This avoids making marquee initialization dependent on an
   * IntersectionObserver callback or IntersectionObserver rootBounds.
   *
   * @returns {{
   *   numberOfCopies: number,
   *   isHorizontalResize: boolean
   * }}
   */
  #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;
    const firstItem = marqueeItems[0];

    if (!firstItem) {
      return {
        numberOfCopies: 1,
        isHorizontalResize: false
      };
    }

    const marqueeWidth =
      this.getBoundingClientRect().width ||
      this.clientWidth ||
      window.innerWidth ||
      0;

    const marqueeItemsWidth =
      firstItem.getBoundingClientRect().width ||
      firstItem.scrollWidth ||
      0;

    const isHorizontalResize =
      this.#marqueeWidth !== marqueeWidth;

    this.#marqueeWidth = marqueeWidth;

    const numberOfCopies =
      marqueeItemsWidth > 0 && marqueeWidth > 0
        ? Math.max(
            1,
            Math.ceil(
              marqueeWidth / marqueeItemsWidth
            )
          )
        : 1;

    return {
      numberOfCopies,
      isHorizontalResize
    };
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const configuredSpeedFactor =
      Number(
        this.getAttribute(
          'data-speed-factor'
        )
      );

    const speedFactor =
      Number.isFinite(configuredSpeedFactor) &&
      configuredSpeedFactor > 0
        ? configuredSpeedFactor
        : 25;

    const safeNumberOfCopies =
      Number.isFinite(numberOfCopies) &&
      numberOfCopies > 0
        ? numberOfCopies
        : 1;

    return (
      Math.sqrt(safeNumberOfCopies) *
      speedFactor
    );
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;

    if (!marqueeItems.length) {
      return;
    }

    /*
     * Allow responsive layout to settle before measuring again.
     */
    await this.#nextFrame();

    const {
      numberOfCopies: newNumberOfCopies,
      isHorizontalResize
    } = this.#queryNumberOfCopies();

    if (!isHorizontalResize) {
      return;
    }

    const currentNumberOfCopies =
      marqueeItems.length;

    const speed =
      this.#calculateSpeed(
        newNumberOfCopies
      );

    if (
      newNumberOfCopies >
      currentNumberOfCopies
    ) {
      this.#addRepeatedItems(
        newNumberOfCopies -
          currentNumberOfCopies
      );
    } else if (
      newNumberOfCopies <
      currentNumberOfCopies
    ) {
      this.#removeRepeatedItems(
        currentNumberOfCopies -
          newNumberOfCopies
      );
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations =
      this.refs.wrapper.getAnimations?.() ?? [];

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone =
      /** @type {HTMLElement} */ (
        this.refs.content.cloneNode(true)
      );

    clone.setAttribute(
      'aria-hidden',
      'true'
    );

    clone.removeAttribute('ref');

    /*
     * Remove ref attributes from cloned descendants as well so the
     * duplicate content cannot be mistaken for live component refs.
     */
    clone
      .querySelectorAll('[ref]')
      .forEach((element) => {
        element.removeAttribute('ref');
      });

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const {
      content,
      marqueeItems
    } = this.refs;

    if (!marqueeItems[0]) {
      return;
    }

    const copiesToAdd =
      Math.max(0, numberOfCopies - 1);

    for (
      let index = 0;
      index < copiesToAdd;
      index += 1
    ) {
      const clone =
        marqueeItems[0].cloneNode(true);

      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children =
      Array.from(content.children);

    const itemsToRemove =
      Math.min(
        Math.max(0, numberOfCopies),
        Math.max(0, children.length - 1)
      );

    for (
      let index = 0;
      index < itemsToRemove;
      index += 1
    ) {
      content.lastElementChild?.remove();
    }
  }

  /**
   * Wait until the browser has completed another rendering frame.
   *
   * @returns {Promise<void>}
   */
  #nextFrame() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        resolve();
      });
    });
  }
}

/**
 * Animate a numeric value smoothly.
 *
 * @param {object} parameters
 * @param {number} parameters.from
 * @param {number} parameters.to
 * @param {number} parameters.duration
 * @param {(value: number) => void} parameters.onUpdate
 * @param {(progress: number) => number} [parameters.easing]
 * @param {() => void} [parameters.onComplete]
 * @returns {{ cancel: () => void, readonly current: number }}
 */
function animateValue({
  from,
  to,
  duration,
  onUpdate,
  easing = (progress) =>
    progress *
    progress *
    (3 - 2 * progress),
  onComplete
}) {
  const startTime =
    performance.now();

  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime
   */
  function animate(currentTime) {
    if (cancelled) {
      return;
    }

    const elapsed =
      currentTime - startTime;

    const progress =
      Math.min(
        elapsed / duration,
        1
      );

    const easedProgress =
      easing(progress);

    currentValue =
      from +
      (to - from) *
        easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (
      typeof onComplete === 'function'
    ) {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },

    cancel() {
      cancelled = true;
    }
  };
}

if (
  !customElements.get(
    'bandholz-marquee-component'
  )
) {
  customElements.define(
    'bandholz-marquee-component',
    BandholzMarqueeComponent
  );
}